const SUMMARY_DATA_KEYS = new Set(['call_summary', 'callSummary']);

export interface CollectedDataEntry {
  key: string;
  data: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isDisplayableCollectedDataEntry(key: string, data: unknown): boolean {
  if (SUMMARY_DATA_KEYS.has(key)) return false;
  if (data == null) return false;
  if (isRecord(data) && Object.prototype.hasOwnProperty.call(data, 'value')) {
    return data.value !== null;
  }
  return true;
}

/**
 * Merge analysis-collected data with provider-assigned collected variables.
 *
 * Tool-assigned variables are added to the same operator-facing data list as
 * analysis data. When both sources use the same key, the tool value wins
 * because it comes from a deterministic runtime assignment instead of a
 * post-call extraction pass.
 */
export function buildCollectedDataEntries(
  dataCollected?: Record<string, unknown> | null,
  collectedVariables?: Record<string, unknown> | null,
): CollectedDataEntry[] {
  const entriesByKey = new Map<string, unknown>();

  const addEntries = (source?: Record<string, unknown> | null) => {
    if (!source) return;
    Object.entries(source).forEach(([key, data]) => {
      if (!isDisplayableCollectedDataEntry(key, data)) return;
      entriesByKey.set(key, data);
    });
  };

  addEntries(dataCollected);
  addEntries(collectedVariables);

  return Array.from(entriesByKey.entries()).map(([key, data]) => ({ key, data }));
}
